import type { ProjectTask } from '../types';

export type SupplierPaymentTaskOption = {
  id: string;
  title: string;
  groupName: string;
  isSubtask: boolean;
  parentTaskId?: string;
  parentTitle?: string;
};

/** Label for payment-term task dropdowns (native select — no CSS on options). */
export function formatSupplierPaymentTaskOptionLabel(opt: SupplierPaymentTaskOption): string {
  const groupPrefix = opt.groupName ? `${opt.groupName} — ` : '';
  if (opt.isSubtask) {
    const parentHint = opt.parentTitle ? ` · ${opt.parentTitle}` : '';
    return `${groupPrefix}↳ ${opt.title} (subtask${parentHint})`;
  }
  return `${groupPrefix}${opt.title}`;
}

function compareSupplierPaymentTaskOptions(a: SupplierPaymentTaskOption, b: SupplierPaymentTaskOption): number {
  const byGroup = a.groupName.localeCompare(b.groupName);
  if (byGroup !== 0) return byGroup;

  const familyA = a.isSubtask ? a.parentTitle || a.title : a.title;
  const familyB = b.isSubtask ? b.parentTitle || b.title : b.title;
  const byFamily = familyA.localeCompare(familyB);
  if (byFamily !== 0) return byFamily;

  if (a.isSubtask !== b.isSubtask) return a.isSubtask ? 1 : -1;
  return a.title.localeCompare(b.title);
}

export function buildSupplierPaymentTaskOptions(args: {
  projectId: string;
  tasks: ProjectTask[];
  groupNameById: Map<string, string>;
}): SupplierPaymentTaskOption[] {
  const { projectId, tasks, groupNameById } = args;
  const titleById = new Map<string, string>();
  for (const t of tasks) {
    if (t.projectId === projectId && t.id) {
      titleById.set(t.id, (t.title || '').trim() || 'Untitled task');
    }
  }

  const options: SupplierPaymentTaskOption[] = [];
  for (const t of tasks) {
    if (t.projectId !== projectId || !t.id) continue;
    const parentTaskId = t.parentTaskId?.trim() || undefined;
    const isSubtask = !!parentTaskId;
    const parentTitle = parentTaskId ? titleById.get(parentTaskId) : undefined;
    options.push({
      id: t.id,
      title: (t.title || '').trim() || 'Untitled task',
      groupName: (t.groupId && groupNameById.get(t.groupId)) || '',
      isSubtask,
      parentTaskId,
      parentTitle,
    });
  }

  return options.sort(compareSupplierPaymentTaskOptions);
}
